'use client';

import dayjs from '@schuelerhilfe/shared/src/utils/dayjs';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { EkomiBadge } from './EkomiBadge';
import { buildReviewsJsonLd, mergeJsonLdReviewItems } from './ReviewsJsonLd';
import { StarRating } from './StarRating';
import type { ReviewItem, ReviewsWidgetResolved } from './types';
import { Button } from '@/components/Button';
import { Carousel } from '@/components/Carousel';
import { JsonLdScript } from '@/components/JsonLdScript';
import { Modal } from '@/components/Modal';
import { withAssetPrefix } from '@/lib/assetPrefix';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';

const TRUNCATE_LENGTH = 250;
const LOAD_MORE_THRESHOLD = 8;

const formatDate = (iso: string, locale: string): string => {
  const parsed = dayjs(iso).locale(locale);
  return parsed.isValid() ? parsed.format('LL') : '';
};

const ReviewSlide = ({
  review,
  locale,
  moreLabel,
  byAuthorLabel,
}: {
  review: ReviewItem;
  locale: string;
  moreLabel: string;
  byAuthorLabel?: string;
}) => {
  const [expanded, setExpanded] = useState(false);

  const text = review.reviewBody ?? '';
  const truncatable = text.length > TRUNCATE_LENGTH;
  const shown =
    truncatable && !expanded
      ? `${text.slice(0, TRUNCATE_LENGTH).trimEnd()}…`
      : text;

  return (
    <div className="px-6">
      <div className="flex justify-center">
        <StarRating rating={review.rating} />
      </div>
      <div className="text-muted mt-1 text-xs">
        {formatDate(review.publishTime, locale)}
        {byAuthorLabel ? ` · ${byAuthorLabel}` : ''}
      </div>
      <blockquote className="mt-3 text-base">
        {shown}
        {truncatable && !expanded && (
          <>
            {' '}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-primary font-bold"
            >
              {moreLabel}
            </button>
          </>
        )}
      </blockquote>
    </div>
  );
};

export const ReviewsWidgetComponent = ({
  resolved,
}: {
  resolved: ReviewsWidgetResolved;
}) => {
  const t = useTranslations('reviews');
  const locale = useLocale();
  const { entities } = useRenderContext();
  const { canonicalOrigin, siteName } = useRuntimeConfig();
  const slug = entities.location?.slug;

  const [modalOpen, setModalOpen] = useState(false);

  const { summary, sliderItems: items, modalItems, anchor } = resolved;

  if (items.length === 0) {
    return null;
  }

  const openModal = () => {
    pushCmsEventTracking(resolved.loadMoreTracking, { label: slug ?? '' });
    setModalOpen(true);
  };

  const trackPrev = () => {
    pushCmsEventTracking(resolved.previousTracking, { label: slug ?? '' });
  };

  const trackNext = () => {
    pushCmsEventTracking(resolved.nextTracking, { label: slug ?? '' });
  };

  const showLoadMore = summary.qualifiedCount > LOAD_MORE_THRESHOLD;
  const displayRating =
    summary.googleAverageRating ??
    entities.location?.googleAverageRating ??
    null;

  // On a Standort page the rating is part of School Details' LocalBusiness
  // node (SH-468); a second block here would publish it twice. On a City Page
  // the summary is that city's, and the only node available here is the
  // brand's Organization — publishing a city's average as the brand's would
  // state the wrong number, and every city page would state a different one
  // for the same `@id`. On the homepage, the route's own JsonLD-Homepage
  // block (10.03.03.34) already publishes a rating-less Organization under
  // that same `@id` — a second, rating-bearing one here would describe the
  // same brand twice (SH-541).
  const jsonLd =
    entities.location ||
    resolved.scope === 'city' ||
    resolved.scope === 'homepage'
      ? null
      : buildReviewsJsonLd(summary, mergeJsonLdReviewItems(items, modalItems), {
          name: siteName,
          url: canonicalOrigin,
        });

  return (
    // Two boxes: the outer one carries the widget's `pt-4` rhythm, the inner
    // one is the sand card with its own padding. A `pt-*` on the card itself
    // would double as both, so the gap before the widget could not be changed
    // without moving the card's content (70.12-Widget-Spacing).
    //
    // The anchor sits on the outer box, so a `#...` link lands above the card
    // rather than inside it; `scroll-mt-20` keeps the sticky header off it
    // (10.03.03.17).
    <div id={anchor || undefined} className="scroll-mt-20 pt-4">
      <section className="bg-secondary-lighter rounded-xl pt-4 pb-4">
        {jsonLd && <JsonLdScript data={jsonLd} />}
        <div className="mx-auto max-w-4xl text-center">
          {resolved.heading && <h2 className="mb-6">{resolved.heading}</h2>}

          <div className="mx-auto max-w-2xl">
            <Carousel
              slidesPerView={1}
              showDots={false}
              showArrows
              prevLabel={t('prev')}
              nextLabel={t('next')}
              onPrev={trackPrev}
              onNext={trackNext}
              slides={items.map((review, i) => (
                <ReviewSlide
                  key={i}
                  review={review}
                  locale={locale}
                  moreLabel={t('showMore')}
                  byAuthorLabel={
                    review.authorName
                      ? t('byAuthor', { name: review.authorName })
                      : undefined
                  }
                />
              ))}
            />
          </div>

          {/*
            The Google summary and the eKomi badge stack on mobile and sit on
            one line from `md` (SH-452). The gap above is the widget's own 32px
            step; it used to be padded out by a 200px min-height on the slide,
            which left a visible hole under a short review.
          */}
          <div className="mt-8 flex flex-col items-center md:flex-row md:items-center md:justify-center md:gap-8">
            {/*
              The rating and its review count are one group, so the row holds
              two items rather than three — the count belongs under the rating
              it describes, not beside the eKomi badge.
            */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Image
                  src={withAssetPrefix('/icons/google.svg')}
                  alt="Google"
                  width={28}
                  height={28}
                  className="shrink-0"
                />
                {displayRating !== null && (
                  <>
                    <span className="text-lg font-bold">
                      {displayRating.toFixed(1)}
                    </span>
                    <StarRating rating={displayRating} />
                  </>
                )}
              </div>
              <span className="text-sm">
                {t('fromNReviews', { count: summary.googleTotalReviewCount })}
              </span>
            </div>

            {/*
              eKomi's badge sits under the Google summary on mobile — the
              arrangement the legacy site used (SH-403) — and beside it from
              `md` (SH-452). It is rendered by eKomi's own script, so nothing
              below this point is ours.
            */}
            {resolved.ekomi && <EkomiBadge {...resolved.ekomi} />}
          </div>

          {showLoadMore && (
            <div className="mt-6 flex justify-center">
              <Button variant="secondary" onClick={openModal}>
                {t('loadMore')}
              </Button>
            </div>
          )}
        </div>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          ariaLabel={t('modalTitle')}
        >
          {modalItems.length === 0 ? (
            <p className="py-8 text-center">{t('empty')}</p>
          ) : (
            <div className="flex flex-col gap-6">
              {modalItems.map((review, i) => (
                <div key={i} className="border-b pb-4 last:border-b-0">
                  <StarRating rating={review.rating} />
                  <div className="text-muted mt-1 text-xs">
                    {formatDate(review.publishTime, locale)}
                    {review.authorName
                      ? ` · ${t('byAuthor', { name: review.authorName })}`
                      : ''}
                  </div>
                  {review.reviewBody && (
                    <p className="mt-2 text-base">{review.reviewBody}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal>
      </section>
    </div>
  );
};
